import { useEffect, useRef, useState } from 'react';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import PaginationComponent from '../../components/pagination';
import { ICurrency } from '../../../libraries/currencies';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { fetchWithdrawSets, IWithdrawSets } from '../../../libraries/brisa';
import { calculateTotal, formatCurrAmount } from '../../utils/script';
import AddEditWithdrawSet from './edit-withdraw-set';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import './styles.scss';

const WithdrawSetList = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = useRef(new AbortController()).current;
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [withdrawSetList, setWithdrawSetList] = useState<IWithdrawSets[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const [selectedWithdrawSet, setSelectedWithdrawSet] = useState<IWithdrawSets | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit'>('edit');

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadTransactionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadTransactionList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setWithdrawSetList([]);
    setTotalRows(0);

    fetchWithdrawSets(filterBarData.entity || 0, controller)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setWithdrawSetList(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //----------------------------------------------------------------//

  const handleRowClick = (withdrawSet: IWithdrawSets) => {
    setSelectedWithdrawSet(withdrawSet);
    setModalMode('edit');
    setModalOpened(true);
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleNewWithdrawSet = () => {
    setSelectedWithdrawSet(null);
    setModalMode('new');
    setModalOpened(true);
  };

  return (
    <div className="withdraw-sets">
      {modalOpened && (
        <AddEditWithdrawSet
          withdrawSet={selectedWithdrawSet || undefined}
          mode={modalMode}
          opened={modalOpened}
          withdrawSetList={withdrawSetList}
          defaultEntid={filterBarData?.entity}
          close={() => {
            setModalOpened(false);
            setSelectedWithdrawSet(null);
          }}
          onSaved={() => {
            setModalOpened(false);
            setSelectedWithdrawSet(null);
            loadTransactionList();
          }}
        />
      )}

      <Header pageTitle={lang.BRISA_WITHDRAW_SETS_TITLE} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={loadTransactionList}
        fieldsUsage={{
          entity: true
        }}
        addButton={handleNewWithdrawSet}
      />

      <div ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={500} ref={tableRef}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_ENTITY}</Table.Th>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_ID}</Table.Th>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_DESCRIPTION}</Table.Th>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_AMOUNT}</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={16}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {!withdrawSetList.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    withdrawSetList?.map((withdrawSet, index) => {
                      const matchingCurrency = currencies?.[0];
                      return (
                        <Table.Tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(withdrawSet)}>
                          <Table.Td>{withdrawSet.entid}</Table.Td>
                          <Table.Td>{withdrawSet.id}</Table.Td>
                          <Table.Td>{withdrawSet.description}</Table.Td>
                          <Table.Td>
                            {formatCurrAmount(calculateTotal(withdrawSet.mc, matchingCurrency), matchingCurrency)}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default WithdrawSetList;
